import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { aMapZoomNextListingLoad, MapFiltersType, useSetAtom } from '~/atoms'
import { STATE_LABELS } from '~/utils'
import {
  MapAddressSearchFilter,
  MapCityFilter,
  MapCountyFilter,
  MapPostalCodeFilter,
  MapStateFilter,
  MapSubmarketsFilter,
  MapZoningFilter,
} from '.'

async function getLocationFilters() {
  const response = await fetch('/api/filters', {
    method: 'POST',
    body: JSON.stringify({
      include: {
        zoning: true,
      },
    }),
  })
  const result = await response.json()
  if (!result.success) {
    console.error('Error getting location filters', result.error)
    throw new Error(`There was a problem fetching results: ${result.error}`)
  }
  return result
}

export default function LocationFilters({
  filters,
  updateFilter,
}: {
  filters: MapFiltersType
  updateFilter: (updates: Record<string, any>) => void
}) {
  const setMapZoomNextListingLoad = useSetAtom(aMapZoomNextListingLoad)
  // Load organization zoning values for the zoning filter.
  const filtersQuery = useQuery({
    queryKey: ['location-filters', 'disable-cache'],
    queryFn: () => getLocationFilters(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  const zoningValues = useMemo(
    () => filtersQuery.data?.zoning || [],
    [filtersQuery.data]
  )
  const stateValues = useMemo(() => Object.keys(STATE_LABELS), [])

  const updateFiltersWithZoomTo = useCallback(
    (updates: Record<string, any>) => {
      updateFilter(updates)
      if (
        Object.keys(updates).length > 1 ||
        (Object.keys(updates).length === 1 && Object.values(updates)[0])
      ) {
        setMapZoomNextListingLoad(true)
      }
    },
    [updateFilter, setMapZoomNextListingLoad]
  )

  return (
    <>
      <MapAddressSearchFilter
        addressSearch={filters.addressSearch}
        updateFilter={updateFiltersWithZoomTo}
      />

      <MapCityFilter
        filterCity={filters.cities || []}
        filterState={filters.states || []}
        updateFilter={updateFiltersWithZoomTo}
      />

      <MapCountyFilter
        filterCounty={filters.counties || []}
        filterState={filters.states || []}
        updateFilter={updateFiltersWithZoomTo}
      />

      <MapPostalCodeFilter
        filterPostalCode={filters.postalCodes || []}
        filterState={filters.states || []}
        updateFilter={updateFiltersWithZoomTo}
      />
      {stateValues.length > 1 && (
        <MapStateFilter
          stateValues={stateValues}
          filterState={filters.states || []}
          updateFilter={updateFiltersWithZoomTo}
        />
      )}

      <MapSubmarketsFilter
        submarkets={filters.submarkets || []}
        updateFilter={updateFilter}
      />
      <MapZoningFilter
        zoningValues={zoningValues}
        filterZoning={filters.zoning || []}
        updateFilter={updateFilter}
      />
    </>
  )
}
